/**
 * ハイライトリールの画像プリロード
 *
 * Immich のサムネイルは JWT 認証が要るため `<img src>` 直指定ができず blob 取得になる。
 * シーンが切り替わってから読み始めると「映らないまま次へ」になってしまうので、
 * 先のシーンぶんを先読みし、現在の画像が揃うまで再生側を待たせる。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { apiClient } from '@/api/client'
import { useSignedUrls } from '@/hooks/useSignedUrl'

/** 現在シーンから何枚先まで先読みするか */
const PRELOAD_AHEAD = 4

/** Place 画像を同時に何枚まで取りに行くか */
const PLACE_PRELOAD_CONCURRENCY = 4

export type ReelImage = {
  url: string
  /** 幅 / 高さ。cover と contain の出し分けに使う */
  aspect: number
}

type Args = {
  /** シーン index と対応する assetId（画像を持たないシーンは null） */
  assetIds: (string | null)[]
  index: number
  enabled: boolean
}

/** デコードまで済ませて縦横比を測る（表示時のちらつきも防げる） */
const measure = (url: string): Promise<number> =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img.naturalHeight === 0 ? 1 : img.naturalWidth / img.naturalHeight)
    img.onerror = () => reject(new Error('image decode failed'))
    img.src = url
  })

export const useReelImages = ({ assetIds, index, enabled }: Args) => {
  const [loaded, setLoaded] = useState<ReadonlyMap<string, ReelImage>>(new Map())
  const [failedIds, setFailedIds] = useState<ReadonlySet<string>>(new Set())
  const inflight = useRef(new Set<string>())
  // 解放対象の Blob URL。レンダリングには使わないので ref で持つ
  const createdUrls = useRef<string[]>([])
  // 破棄後に解決した取得を捨てるための世代番号
  const generation = useRef(0)

  useEffect(() => {
    if (!enabled) return

    const gen = generation.current
    const targets = assetIds.slice(index, index + PRELOAD_AHEAD + 1).filter((id): id is string => id !== null)

    for (const id of targets) {
      if (inflight.current.has(id)) continue
      inflight.current.add(id)

      apiClient
        .get<Blob>(`/immich/thumbnail/${id}?size=preview`, { responseType: 'blob' })
        .then(async (res) => {
          const url = URL.createObjectURL(res.data)
          if (gen !== generation.current) {
            URL.revokeObjectURL(url)
            return
          }
          createdUrls.current.push(url)
          const aspect = await measure(url)
          setLoaded((prev) => new Map(prev).set(id, { url, aspect }))
        })
        .catch(() => {
          if (gen !== generation.current) return
          setFailedIds((prev) => new Set(prev).add(id))
        })
    }
  }, [assetIds, index, enabled])

  // 閉じたら Blob URL をまとめて解放する
  useEffect(() => {
    if (enabled) return

    generation.current += 1
    for (const url of createdUrls.current) URL.revokeObjectURL(url)
    createdUrls.current = []
    inflight.current.clear()
    setLoaded(new Map())
    setFailedIds(new Set())
  }, [enabled])

  useEffect(
    () => () => {
      generation.current += 1
      for (const url of createdUrls.current) URL.revokeObjectURL(url)
      createdUrls.current = []
    },
    [],
  )

  const currentId = assetIds.at(index) ?? null
  /** 読み込み完了か失敗が確定していれば true（これ以上待つ意味がない） */
  const isCurrentSettled = !currentId || loaded.has(currentId) || failedIds.has(currentId)

  const getImage = useCallback((id: string | null) => (id === null ? null : (loaded.get(id) ?? null)), [loaded])

  return { getImage, isCurrentSettled }
}

/**
 * Place 画像の先読み
 *
 * 署名付き URL なので表示時に署名取得 → 画像取得の 2 往復が入る。
 * リールを開いた時点でまとめて署名を取り、章の順に blob として取得しておく。
 * blob URL にしておけば表示時に再リクエストされない（`new Image()` の先読みだけだと
 * 参照が消えた時点でキャッシュから落ち、`<img>` 生成時に取り直しになる）。
 * 全部を同時に投げるとブラウザの同時接続上限で最初の章が後回しになるため、
 * 同時実行数を絞って先頭から確実に埋める。
 * 取得できなかった画像は返さないので、呼び出し側は旅の写真へフォールバックできる。
 */
export const usePlaceImages = ({ paths, enabled }: { paths: string[]; enabled: boolean }) => {
  // 配列の同一性ではなく中身でメモ化する。リールは 50ms ごとに再描画されるので、
  // 呼び出し側の配列が作り直されるたびに先読みのループが中断されるのを防ぐ
  const orderKey = enabled ? paths.join('\n') : ''
  const targets = useMemo(() => (orderKey === '' ? [] : orderKey.split('\n')), [orderKey])
  const { urls } = useSignedUrls(targets)
  const [ready, setReady] = useState<ReadonlyMap<string, string>>(new Map())

  useEffect(() => {
    const signed = new Map(Object.entries(urls))
    const queue = targets.filter((path) => signed.has(path))
    if (queue.length === 0) return

    let cancelled = false
    let cursor = 0
    const created: string[] = []

    const worker = async (): Promise<void> => {
      while (!cancelled) {
        const path = queue.at(cursor)
        cursor += 1
        if (path === undefined) return
        const url = signed.get(path)
        if (url === undefined) continue

        const res = await fetch(url).catch(() => null)
        if (!res?.ok) continue
        const blob = await res.blob().catch(() => null)
        if (!blob) continue
        if (cancelled) return

        const objectUrl = URL.createObjectURL(blob)
        created.push(objectUrl)
        setReady((prev) => new Map(prev).set(path, objectUrl))
      }
    }

    for (let i = 0; i < Math.min(PLACE_PRELOAD_CONCURRENCY, queue.length); i += 1) void worker()

    return () => {
      cancelled = true
      for (const objectUrl of created) URL.revokeObjectURL(objectUrl)
    }
  }, [targets, urls])

  return useCallback((path: string | null) => (path === null ? null : (ready.get(path) ?? null)), [ready])
}
